import React, { Fragment, useEffect, useState } from 'react';
import {
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Label,
    Row,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Badge,
    Spinner,
    Table,
} from 'reactstrap';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import FeatherIconCom from '../../../Utils/CommonComponents/CommonIcons/FeatherIconCom';
import CardHeaderSpan from '../../../Utils/CommonComponents/CardHeaderSpan';
import { API } from '../../../api/API';
import { getLabel } from '../../../sharedFunctions/CommonLable';
import { getData } from '../../../CommonElements/Functions/PostGetFun';
import Loader from '../../../Layout/Loader';

interface PresetOption {
    id: number;
    label: string;
}

const PRESET_OPTIONS: PresetOption[] = [
    { label: 'Current month', id: 0 },
    { label: 'Fortnight', id: 5 },
    { label: 'Last month', id: 4 },
    { label: 'Last 3 months', id: 1 },
    { label: 'Half yearly', id: 2 },
    { label: 'Yearly', id: 3 },
];

export default function BrandCampaignContainer() {
    const navigate = useNavigate();
    const serverUrl = localStorage.getItem('serverUrl');
    const APP_TOKEN = localStorage.getItem('appToken');

    const [fromdate, setFromdate] = useState(
        moment().startOf('year').format('YYYY-MM-DD')
    );
    const [todate, setToDate] = useState(
        moment().endOf('year').format('YYYY-MM-DD')
    );
    const [campaignStatus, setCampaignStatus] = useState<string>('All');
    const [statusOptions, setStatusOptions] = useState<{ id: string; label: string }[]>([
        { id: 'All', label: 'All' }
    ]);
    const [selectedPreset, setSelectedPreset] = useState<number>(3); // Yearly by default

    const [campaigns, setCampaigns] = useState<any[]>([]);
    const [displayedCampaigns, setDisplayedCampaigns] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchText, setSearchText] = useState<string>('');

    // Modal state for Call Planned and Call Done outlets
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [modalTitle, setModalTitle] = useState<string>('');
    const [modalData, setModalData] = useState<any[]>([]);

    const toggleModal = () => setModalOpen(!modalOpen);

    const handleOpenPlanModal = (outlets: any[]) => {
        setModalTitle('Call Planned Outlets');
        setModalData(outlets || []);
        setModalOpen(true);
    };

    const handleOpenDoneModal = (outlets: any[]) => {
        setModalTitle('Call Done Outlets');
        setModalData(outlets || []);
        setModalOpen(true);
    };

    // Fetch Statuses
    useEffect(() => {
        const fetchStatuses = async () => {
            if (!serverUrl || !APP_TOKEN) return;
            try {
                const url = `${serverUrl}${API.getCampaignStatus}`;
                const { status, data } = await getData(url, true);
                if (status === 200 && data?.data) {
                    let formatted: { id: string; label: string }[] = [];
                    if (typeof data.data === 'object' && data.data !== null) {
                        formatted = Object.entries(data.data).reduce((arr: { id: string; label: string }[], [key, value]) => {
                            if (key !== 'Draft') {
                                arr.push({ label: key, id: String(value) });
                            }
                            return arr;
                        }, []);
                    }
                    formatted.unshift({ id: 'All', label: 'All' });
                    setStatusOptions(formatted);
                }
            } catch (err) {
                console.error('Error fetching campaign statuses:', err);
            }
        };
        fetchStatuses();
    }, []);

    // Filter preset change handler
    const handleFilterPreset = (key: number) => {
        setSelectedPreset(key);
        let startDate = '';
        let endDate = '';
        if (key === 0) {
            startDate = moment().startOf('month').format('YYYY-MM-DD');
            endDate = moment().endOf('month').format('YYYY-MM-DD');
        } else if (key === 1) {
            startDate = moment().subtract(3, 'month').startOf('month').format('YYYY-MM-DD');
            endDate = moment().format('YYYY-MM-DD');
        } else if (key === 2) {
            startDate = moment().subtract(6, 'month').startOf('month').format('YYYY-MM-DD');
            endDate = moment().format('YYYY-MM-DD');
        } else if (key === 3) {
            startDate = moment().startOf('year').format('YYYY-MM-DD');
            endDate = moment().endOf('year').format('YYYY-MM-DD');
        } else if (key === 4) {
            startDate = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
            endDate = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        } else if (key === 5) {
            startDate = moment().subtract(15, 'days').format('YYYY-MM-DD');
            endDate = moment().format('YYYY-MM-DD');
        }
        setFromdate(startDate);
        setToDate(endDate);
    };

    // Fetch Brand Campaigns
    const fetchCampaigns = async () => {
        if (!serverUrl || !APP_TOKEN) return;
        setLoading(true);
        try {
            const url = `${serverUrl}${API.brand_campaign}`;
            const params = {
                start_date: fromdate,
                end_date: todate,
                status: campaignStatus,
            };
            const { status, data } = await getData(url, true, params);
            if (status === 200 && Array.isArray(data?.data)) {
                setCampaigns(data.data);
            } else {
                setCampaigns([]);
            }
        } catch (err) {
            console.error('Error fetching brand campaigns:', err);
            setCampaigns([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaigns();
    }, [fromdate, todate, campaignStatus]);

    // Search filter logic
    useEffect(() => {
        const lower = searchText.toLowerCase().trim();
        if (!lower) {
            setDisplayedCampaigns(campaigns);
        } else {
            const filtered = campaigns.filter((item) => {
                const nameMatch = item?.CampiagnName?.toLowerCase().includes(lower);
                const statusMatch = item?.Status?.toLowerCase().includes(lower);
                const focusMatch = Array.isArray(item?.FocusBrands)
                    ? item.FocusBrands.join(' ').toLowerCase().includes(lower)
                    : false;
                return nameMatch || statusMatch || focusMatch;
            });
            setDisplayedCampaigns(filtered);
        }
    }, [searchText, campaigns]);

    // Status Badge helper
    const getStatusBadge = (statusStr: string) => {
        switch (statusStr) {
            case 'Published':
                return <Badge color="warning" className="text-dark"><FeatherIconCom iconName="Clock" size={12} className="me-1" />{statusStr}</Badge>;
            case 'Started':
                return <Badge color="primary"><FeatherIconCom iconName="PlayCircle" size={12} className="me-1" />{statusStr}</Badge>;
            case 'Closed':
                return <Badge color="success"><FeatherIconCom iconName="CheckCircle" size={12} className="me-1" />{statusStr}</Badge>;
            default:
                return <Badge color="secondary">{statusStr || 'N/A'}</Badge>;
        }
    };

    // Export to Excel
    const handleExportToExcel = () => {
        const exportData = displayedCampaigns.map((row) => ({
            'Event Name': row?.CampiagnName || '-',
            'Start Date': row?.StartDate ? moment(row.StartDate).format('DD MMM YYYY') : '-',
            'End Date': row?.EndDate ? moment(row.EndDate).format('DD MMM YYYY') : '-',
            'Locking Date': row?.LockingDate ? moment(row.LockingDate).format('DD MMM YYYY') : '-',
            'Outlet Count': row?.DoctorCount ?? '-',
            'Focus Brand': Array.isArray(row?.FocusBrands) ? row.FocusBrands.join(', ') : '-',
            'Status': row?.Status || '-',
            'Call Planned Count': row?.Plan ?? 0,
            'Call Done Count': row?.Done ?? 0,
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Brand Campaigns');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, `Brand_Campaigns_${moment().format('YYYY-MM-DD')}.xlsx`);
    };

    return (
        <CardBody className="p-3" style={{ color: '#000000' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                    <FeatherIconCom iconName="Radio" size={20} className="text-primary" />
                    <span>Brand Campaign</span>
                </h5>
            </div>

            {/* Filters Bar */}
            <div className="row g-2 mb-3 align-items-end">
                <div className="col-md-3 col-sm-6">
                    <Label className="form-label mb-1 text-muted small fw-semibold">Search:</Label>
                    <Input
                        type="text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder="Search campaign, brand..."
                        className="form-control form-control-sm"
                    />
                </div>
                <div className="col-md-2 col-sm-6">
                    <Label className="form-label mb-1 text-muted small fw-semibold">Duration:</Label>
                    <Input
                        type="select"
                        className="form-select form-select-sm"
                        value={selectedPreset}
                        onChange={(e) => handleFilterPreset(Number(e.target.value))}
                    >
                        {PRESET_OPTIONS.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                                {opt.label}
                            </option>
                        ))}
                    </Input>
                </div>
                <div className="col-md-2 col-sm-6">
                    <Label className="form-label mb-1 text-muted small fw-semibold">Status:</Label>
                    <Input
                        type="select"
                        className="form-select form-select-sm"
                        value={campaignStatus}
                        onChange={(e) => setCampaignStatus(e.target.value)}
                    >
                        {statusOptions.map((st) => (
                            <option key={st.id} value={st.label}>
                                {st.label}
                            </option>
                        ))}
                    </Input>
                </div>
                <div className="col-md-2 col-sm-6">
                    <Label className="form-label mb-1 text-muted small fw-semibold">From Date:</Label>
                    <Input
                        type="date"
                        className="form-control form-control-sm"
                        value={fromdate}
                        onChange={(e) => setFromdate(e.target.value)}
                    />
                </div>
                <div className="col-md-2 col-sm-6">
                    <Label className="form-label mb-1 text-muted small fw-semibold">To Date:</Label>
                    <Input
                        type="date"
                        className="form-control form-control-sm"
                        value={todate}
                        onChange={(e) => setToDate(e.target.value)}
                    />
                </div>
                <div className="col-auto d-flex gap-2 ms-auto align-self-end">
                    <Button color="primary" size="sm" className="px-3" onClick={fetchCampaigns}>
                        Apply
                    </Button>
                    <Button color="primary" outline size="sm" className="px-3 d-flex align-items-center" onClick={handleExportToExcel}>
                        <FeatherIconCom iconName="Download" size={14} className="me-1" />
                        Export
                    </Button>
                </div>
            </div>

            {/* Main Table */}
            <div className="table-responsive border rounded">
                {loading ? (
                    <Loader height="250px" />
                ) : displayedCampaigns.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <FeatherIconCom iconName="Inbox" size={32} className="mb-2 text-secondary" />
                        <div>No Brand Campaigns found for the selected criteria.</div>
                    </div>
                ) : (
                    <Table hover className="mb-0 align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>Event Name</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Locking Date</th>
                                <th>Outlet Count</th>
                                <th>Focus Brand</th>
                                <th>Status</th>
                                <th>Call Planned</th>
                                <th>Call Done</th>
                                <th className="text-end">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedCampaigns.map((item, idx) => (
                                <tr key={item?.BrandCampiagnId || idx}>
                                    <td className="fw-semibold text-primary">
                                        {item?.CampiagnName || '-'}
                                    </td>
                                    <td>
                                        {item?.StartDate ? moment(item.StartDate).format('DD MMM YYYY') : '-'}
                                    </td>
                                    <td>
                                        {item?.EndDate ? moment(item.EndDate).format('DD MMM YYYY') : '-'}
                                    </td>
                                    <td>
                                        {item?.LockingDate ? moment(item.LockingDate).format('DD MMM YYYY') : '-'}
                                    </td>
                                    <td>
                                        <Badge color="light" className="text-dark border px-2 py-1">
                                            {item?.DoctorCount ?? 0}
                                        </Badge>
                                    </td>
                                    <td>
                                        {Array.isArray(item?.FocusBrands) && item.FocusBrands.length > 0
                                            ? item.FocusBrands.join(', ')
                                            : '-'}
                                    </td>
                                    <td>{getStatusBadge(item?.Status)}</td>
                                    <td>
                                        <button
                                            className="btn btn-xs btn-outline-primary rounded-pill px-3 py-1"
                                            onClick={() => handleOpenPlanModal(item?.PlanOutlets)}
                                        >
                                            {item?.Plan ?? 0}
                                        </button>
                                    </td>
                                    <td>
                                        <button
                                            className="btn btn-xs btn-outline-info rounded-pill px-3 py-1"
                                            onClick={() => handleOpenDoneModal(item?.DoneOutlets)}
                                        >
                                            {item?.Done ?? 0}
                                        </button>
                                    </td>
                                    <td className="text-end">
                                        <Button
                                            color="primary"
                                            size="sm"
                                            className="px-3"
                                            onClick={() => navigate(`/brandCampaign/details/${item?.BrandCampiagnId}`)}
                                        >
                                            <FeatherIconCom iconName="Eye" size={14} className="me-1" />
                                            View
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </div>

            {/* Outlets List Table Modal */}
            <Modal isOpen={modalOpen} toggle={toggleModal} size="lg" centered>
                <ModalHeader toggle={toggleModal}>
                    <FeatherIconCom iconName="List" size={18} className="me-2 text-primary" />
                    {modalTitle}
                </ModalHeader>
                <ModalBody>
                    {modalData && modalData.length > 0 ? (
                        <div className="table-responsive" style={{ maxHeight: '400px' }}>
                            <Table striped borderless hover className="align-middle mb-0">
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th>{getLabel("Outlet")} Name</th>
                                        <th>{getLabel("OutletOrgCode")}</th>
                                        <th>Type</th>
                                        <th>Beat</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modalData.map((outlet: any, idx: number) => (
                                        <tr key={idx}>
                                            <td className="fw-semibold">{outlet?.OutletName || '-'}</td>
                                            <td><code>{outlet?.OutletOrgCode || '-'}</code></td>
                                            <td>
                                                <Badge color="light" className="text-dark border">
                                                    {outlet?.Classification || 'General'}
                                                </Badge>
                                            </td>
                                            <td>{outlet?.BeatName || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-4 text-muted">
                            <FeatherIconCom iconName="Info" size={28} className="mb-2" />
                            <div>No {getLabel("Outlets").toLowerCase()} recorded for this action.</div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" size="sm" onClick={toggleModal}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </CardBody>
    );
}
